/**
 * React hooks for zero-config mode
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { 
  scanForTasks, 
  scanForTasksAsync,
  watchForChanges, 
  type ClientTask, 
  type ScanOptions 
} from './scanner';

export interface UseClientSideScanOptions extends ScanOptions {
  enabled?: boolean;
  watchChanges?: boolean;
  scanInterval?: number;
  includeAsync?: boolean;
}

/**
 * Hook to scan for tasks on the client side
 */
export function useClientSideScan(options: UseClientSideScanOptions = {}) {
  const {
    enabled = true,
    watchChanges: watchChangesOption = true,
    scanInterval,
    ...scanOptions
  } = options;

  const [tasks, setTasks] = useState<ClientTask[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [lastScanTime, setLastScanTime] = useState<Date | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Memoize scanOptions to prevent infinite re-renders
  const stableScanOptions = useMemo(() => scanOptions, [
    scanOptions.includeComments,
    scanOptions.includeHtml,
    scanOptions.includeProps,
    scanOptions.includeSourceMaps,
    scanOptions.includePublicFiles,
    scanOptions.includeAsync,
    scanOptions.maxTasks,
    JSON.stringify(scanOptions.publicFiles) // Serialize array for comparison
  ]);

  // Manual rescan function
  const rescan = useCallback(async () => {
    if (!enabled) return;

    setIsScanning(true);
    try {
      const newTasks = stableScanOptions.includeAsync || stableScanOptions.includeSourceMaps || stableScanOptions.includePublicFiles
        ? await scanForTasksAsync(stableScanOptions)
        : scanForTasks(stableScanOptions);
      setTasks(newTasks);
      setLastScanTime(new Date());
    } finally {
      setIsScanning(false);
    }
  }, [enabled, stableScanOptions]);

  // Set up scanning
  useEffect(() => {
    if (!enabled) {
      setTasks([]);
      return;
    }

    // Initial scan function
    const performInitialScan = async () => {
      setIsScanning(true);
      try {
        const newTasks = stableScanOptions.includeAsync || stableScanOptions.includeSourceMaps || stableScanOptions.includePublicFiles
          ? await scanForTasksAsync(stableScanOptions)
          : scanForTasks(stableScanOptions);
        setTasks(newTasks);
        setLastScanTime(new Date());
      } finally {
        setIsScanning(false);
      }
    };

    // Initial scan
    performInitialScan();

    // Set up change watching
    if (watchChangesOption) {
      cleanupRef.current = watchForChanges((newTasks) => {
        setTasks(newTasks);
        setLastScanTime(new Date());
      }, stableScanOptions);
    }

    // Set up interval scanning if specified
    let intervalId: NodeJS.Timeout;
    if (scanInterval && scanInterval > 0) {
      intervalId = setInterval(() => {
        performInitialScan();
      }, scanInterval);
    }

    // Cleanup
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enabled, watchChangesOption, scanInterval, stableScanOptions]);

  return {
    tasks,
    isScanning,
    lastScanTime,
    rescan,
    taskCount: tasks.length,
  };
}

/**
 * Hook to track feature availability
 */
export function useFeatureDetection() {
  const [features, setFeatures] = useState({
    hasApiRoute: false,
    hasAiEnabled: false,
    hasFileAccess: false,
    isDevMode: false,
  });

  useEffect(() => {
    const detectFeatures = async () => {
      const detected = {
        hasApiRoute: false, // Disable API route check in zero-config mode to avoid 404s
        hasAiEnabled: checkAiEnabled(),
        hasFileAccess: false, // Always false in client-side mode
        isDevMode: checkDevMode(),
      };
      setFeatures(detected);
    };

    // Only check once on mount
    detectFeatures();
  }, []); // Empty dependency array - only run once

  return features;
}

// Cache API route check to avoid repeated 404s
let apiRouteCache: { result: boolean; timestamp: number } | null = null;
const API_ROUTE_CACHE_DURATION = 30000; // 30 seconds

/**
 * Check if API route is available (cached)
 */
async function checkApiRoute(): Promise<boolean> {
  const now = Date.now();
  
  // Return cached result if still valid
  if (apiRouteCache && (now - apiRouteCache.timestamp) < API_ROUTE_CACHE_DURATION) {
    return apiRouteCache.result;
  }
  
  try {
    const response = await fetch('/api/vibeship/health', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const result = response.ok;
    
    // Cache the result
    apiRouteCache = { result, timestamp: now };
    return result;
  } catch (error) {
    // Silently cache the failure - don't log 404s as they're expected in zero-config mode
    apiRouteCache = { result: false, timestamp: now };
    return false;
  }
}

/**
 * Check if AI features are enabled
 */
function checkAiEnabled(): boolean {
  // Check for common AI API key patterns in window object
  if (typeof window !== 'undefined') {
    const win = window as any;
    return !!(
      win.OPENAI_API_KEY ||
      win.ANTHROPIC_API_KEY ||
      win.process?.env?.OPENAI_API_KEY ||
      win.process?.env?.ANTHROPIC_API_KEY
    );
  }
  return false;
}

/**
 * Check if in development mode
 */
function checkDevMode(): boolean {
  if (typeof window !== 'undefined') {
    const win = window as any;
    return (
      win.process?.env?.NODE_ENV === 'development' ||
      win.location?.hostname === 'localhost' ||
      win.location?.hostname === '127.0.0.1'
    );
  }
  return false;
}

/**
 * Hook for progressive enhancement levels
 */
export function useProgressiveLevel() {
  const features = useFeatureDetection();
  const [currentLevel, setCurrentLevel] = useState(0);

  useEffect(() => {
    // Determine current level based on features
    if (features.hasApiRoute && features.hasFileAccess) {
      setCurrentLevel(3); // Advanced
    } else if (features.hasApiRoute) {
      setCurrentLevel(2); // Basic Config
    } else {
      setCurrentLevel(0); // Zero Config
    }
  }, [features]);

  const levels = [
    {
      level: 0,
      name: 'Zero Config',
      description: 'Client-side scanning only',
      features: ['View tasks', 'Read-only mode'],
    },
    {
      level: 1,
      name: 'Basic Config',
      description: 'API route enabled',
      features: ['Task scanning', 'Basic editing', 'File access'],
    },
    {
      level: 2,
      name: 'Custom Config',
      description: 'Configuration file added',
      features: ['Custom paths', 'AI features', 'Advanced patterns'],
    },
    {
      level: 3,
      name: 'Advanced',
      description: 'Full control mode',
      features: ['Custom implementations', 'Enterprise features'],
    },
  ];

  return {
    currentLevel,
    levels,
    features,
    canUpgrade: currentLevel < 3,
  };
}